import { client } from "./supabase";

// ---------------------------------------------------------------------------
// Sección Infraestructura — lectura del inventario de pantallas.
// SOLO LECTURA (Fase 1 · sub-paso 3). No escribe nada.
// ---------------------------------------------------------------------------

// Etiquetas de display (centralizadas — 10.1). El `tipo` guardado está alineado
// a galeria_fotos.screen_type (asc/cir/esp/cow/dir), ver 10.113.
export const TIPO_PANTALLA_LABEL = {
  asc: "Ascensor",
  cir: "Tótem",
  esp: "Hall",
  cow: "Cowork",
  dir: "Cartelera",
};

// Estados de pantalla (vocabulario controlado del CHECK; extensible a futuro).
export const ESTADO_PANTALLA_LABEL = {
  operativa: "Operativa",
  precisa_mantenimiento: "Precisa mantenimiento",
};

const conPais = (query, pais) => (pais && pais !== "Region" ? query.eq("pais", pais) : query);

// Catálogo de bloques (agrupación de ruta), ordenado.
export const getBloquesInfra = async (pais) => {
  const { data, error } = await conPais(client.from("bloques").select("*").eq("eliminada", false), pais).order(
    "orden",
    {
      ascending: true,
    }
  );
  if (error) throw error;
  return data || [];
};

// Edificios con su bloque asignado (bloque_id NULL = sin asignar).
export const getEdificiosConBloque = async (pais) => {
  const { data, error } = await conPais(
    client.from("edificios").select("id, edificio, ubicacion, pais, estado, bloque_id").eq("eliminada", false),
    pais
  ).order("edificio", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Fichas de pantalla vivas.
export const getPantallasInfra = async (pais) => {
  const { data, error } = await conPais(
    client.from("pantallas").select("id, edificio_id, tipo, etiqueta, orden, estado, pais").eq("eliminada", false),
    pais
  )
    .order("edificio_id", { ascending: true })
    .order("orden", { ascending: true });
  if (error) throw error;
  return data || [];
};

// Carga combinada, TOLERANTE a que las tablas nuevas todavía no estén aplicadas
// (los seeds/migraciones se aplican aparte, con OK de Ignacio — 10.13/10.137).
// No traga el error en silencio (10.170): lo loguea y lo devuelve en `errores`
// para que la vista pueda distinguir "sin datos" de "falla real".
export const getInventarioInfra = async (pais) => {
  const [b, e, p] = await Promise.allSettled([
    getBloquesInfra(pais),
    getEdificiosConBloque(pais),
    getPantallasInfra(pais),
  ]);
  const errores = [];
  const val = (res, nombre) => {
    if (res.status === "fulfilled") return res.value;
    // eslint-disable-next-line no-console
    console.warn(`[infra] no se pudo leer ${nombre}:`, res.reason?.message || res.reason);
    errores.push(nombre);
    return [];
  };
  return {
    bloques: val(b, "bloques"),
    edificios: val(e, "edificios"),
    pantallas: val(p, "pantallas"),
    errores,
  };
};

// Arma la jerarquía bloque → edificios → pantallas + totales. Comparación por ID
// (10.7). Un edificio aparece si pertenece a un bloque o si tiene pantallas.
export const construirInventarioInfra = ({ bloques, edificios, pantallas }) => {
  const pantallasPorEdificio = new Map();
  pantallas.forEach((pan) => {
    const arr = pantallasPorEdificio.get(pan.edificio_id) || [];
    arr.push(pan);
    pantallasPorEdificio.set(pan.edificio_id, arr);
  });

  const edificioNode = (edi) => {
    const pans = pantallasPorEdificio.get(edi.id) || [];
    return { ...edi, pantallas: pans, totalPantallas: pans.length };
  };

  const gruposBloque = bloques.map((bloque) => {
    const edis = edificios
      .filter((edi) => edi.bloque_id != null && String(edi.bloque_id) === String(bloque.id))
      .map(edificioNode);
    return {
      bloque,
      edificios: edis,
      totalEdificios: edis.length,
      totalPantallas: edis.reduce((acc, edi) => acc + edi.totalPantallas, 0),
    };
  });

  // Edificios con pantallas pero sin bloque asignado (ej. Los Militares, Matta).
  const sinBloqueEdificios = edificios
    .filter((edi) => (edi.bloque_id == null || edi.bloque_id === "") && pantallasPorEdificio.has(edi.id))
    .map(edificioNode);

  const totalPantallas = pantallas.length;
  const totalEdificios = gruposBloque.reduce((acc, g) => acc + g.totalEdificios, 0) + sinBloqueEdificios.length;

  return {
    gruposBloque,
    sinBloque: {
      edificios: sinBloqueEdificios,
      totalEdificios: sinBloqueEdificios.length,
      totalPantallas: sinBloqueEdificios.reduce((acc, edi) => acc + edi.totalPantallas, 0),
    },
    totales: {
      bloques: bloques.length,
      edificios: totalEdificios,
      pantallas: totalPantallas,
    },
  };
};
